import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TileEmpty } from '../TileWrapper';
import { Bell, CheckCircle, Calendar, Users, AlertTriangle, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

const ICON_MAP = {
  leave: Calendar,
  task: CheckCircle,
  team: Users,
  alert: AlertTriangle,
  message: MessageSquare,
  default: Bell,
};

// Mock data for simulation
const MOCK_DATA = {
  items: [
    { id: '1', type: 'task', title: 'Task assigned to you', message: 'Complete safety training review has been assigned', created_date: new Date(Date.now() - 3600000).toISOString(), is_read: false, link_url: '/tasks' },
    { id: '2', type: 'leave', title: 'Leave request approved', message: 'Your annual leave for Dec 27-29 has been approved', created_date: new Date(Date.now() - 7200000).toISOString(), is_read: false, link_url: '/attendance' },
    { id: '3', type: 'alert', title: 'Certificate expiring', message: 'Your First Aid certificate expires in 7 days', created_date: new Date(Date.now() - 86400000).toISOString(), is_read: true, link_url: '/profile' },
    { id: '4', type: 'team', title: 'New team member', message: 'Sarah Johnson joined the Site Alpha team', created_date: new Date(Date.now() - 172800000).toISOString(), is_read: true, link_url: '/company-team' },
    { id: '5', type: 'message', title: 'New message', message: 'Mike Williams sent you a message', created_date: new Date(Date.now() - 259200000).toISOString(), is_read: true, link_url: '/chat' },
  ],
  unreadCount: 2,
};

export function NotificationsTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [data, setData] = useState({ items: [], unreadCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      const limit = size === 'large' ? 8 : size === 'tall' ? 6 : 4;
      setData({
        items: MOCK_DATA.items.slice(0, limit),
        unreadCount: MOCK_DATA.unreadCount,
      });
      setLoading(false);
      return;
    }

    if (!userProfile?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const limit = size === 'large' ? 8 : size === 'tall' ? 6 : 4;

        const { data: notifications, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userProfile.id)
          .order('created_date', { ascending: false })
          .limit(limit);

        if (error) throw error;

        const unread = (notifications || []).filter(n => !n.is_read).length;

        setData({
          items: notifications || [],
          unreadCount: unread,
        });
      } catch (err) {
        console.error('Failed to load notifications:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Subscribe to new notifications
    const channel = supabase
      .channel('notifications-tile')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userProfile.id}`,
        },
        () => loadData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userProfile?.id, size, useMockData]);

  const handleClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notification.id);

      setData(prev => ({
        ...prev,
        items: prev.items.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, prev.unreadCount - 1),
      }));
    }

    // Navigate if there's a link
    if (notification.link_url) {
      navigate(notification.link_url);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-start gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-gray-200" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (data.items.length === 0) {
    return <TileEmpty message="No notifications" icon={Bell} />;
  }

  return (
    <div className="h-full flex flex-col">
      {data.unreadCount > 0 && (
        <div className="flex items-center justify-between mb-2 shrink-0">
          <Badge variant="default">{data.unreadCount} new</Badge>
        </div>
      )}

      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {data.items.map((notification) => {
            const IconComponent = ICON_MAP[notification.type] || ICON_MAP.default;

            return (
              <div
                key={notification.id}
                onClick={() => handleClick(notification)}
                className={cn(
                  "flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                  notification.is_read ? "hover:bg-gray-50" : "bg-blue-50 hover:bg-blue-100"
                )}
              >
                <div className={cn(
                  "p-2 rounded-full shrink-0",
                  notification.is_read ? "bg-gray-100" : "bg-blue-100"
                )}>
                  <IconComponent className={cn(
                    "h-4 w-4",
                    notification.is_read ? "text-gray-500" : "text-blue-600"
                  )} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-sm truncate",
                    notification.is_read ? "text-gray-600" : "font-medium text-gray-900"
                  )}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDistanceToNow(new Date(notification.created_date), { addSuffix: true })}
                  </p>
                </div>

                {!notification.is_read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-2" />
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
